import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle, FileText, Calculator, Car, TrendingUp, RefreshCw,
  Shield, Clock, Phone, Mail, DollarSign, Percent, Award, BarChart
} from 'lucide-react';

const CarRefinance = () => {
  const [principal, setPrincipal] = useState(500000);
  const [interest, setInterest] = useState(9.5);
  const [tenure, setTenure] = useState(5);

  const calculateEMI = () => {
    const p = principal;
    const r = interest / 12 / 100;
    const n = tenure * 12;
    const emi = p * r * Math.pow(1 + r, n) / (Math.pow(1 + r, n) - 1);
    return isNaN(emi) ? 0 : Math.round(emi);
  };

  const emi = calculateEMI();
  const totalPayable = emi * tenure * 12;
  const totalInterest = totalPayable - principal;

  return (
    <div className="bg-gray-50">
      <div className="mb-6">
        <div className="flex justify-center lg:justify-end">
          <img
            src="Car.png"
            alt="Car Refinance"
            className="max-w-full h-full"
          />
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-16 max-w-4xl mx-auto text-center animate-fade-up">
          <h2 className="text-3xl font-bold text-green-700 mb-6">About Car Refinance</h2>
          <p className="text-gray-700 text-lg leading-relaxed">
            Car refinance (or auto loan refinancing) is the process of replacing your existing car loan with a new one, typically with better terms—lower interest rate, reduced monthly EMIs, or a shorter/longer tenure. It can help you save money, improve cash flow, or adjust your repayment structure to match your current financial situation.  CashPro Financial Services compares offers from multiple lenders to secure the most beneficial refinancing deal for you.
          </p>
        </div>
        <div className="mb-16 bg-gradient-to-r from-green-50 to-green-50 rounded-xl p-8 animate-fade-up animation-delay-200">
          <p className="text-lg text-gray-700 mb-6">
            Use our car refinance EMI calculator to estimate your new monthly payments. Adjust the loan amount (the outstanding balance you wish to refinance), new interest rate, and desired tenure to see the EMI, total interest, and total payment. This helps you compare different refinance options.
          </p>
          <div className="flex flex-col md:flex-row gap-8 items-center">
            <div className="flex-1">
              <div className="space-y-4">
                <div>
                  <label className="block text-gray-700 font-medium mb-2">Loan Amount (Outstanding): ₹{principal.toLocaleString()}</label>
                  <input
                    type="range"
                    min="100000"
                    max="3000000"
                    step="10000"
                    value={principal}
                    onChange={(e) => setPrincipal(Number(e.target.value))}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 font-medium mb-2">New Interest Rate: {interest}% p.a.</label>
                  <input
                    type="range"
                    min="7"
                    max="15"
                    step="0.1"
                    value={interest}
                    onChange={(e) => setInterest(Number(e.target.value))}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 font-medium mb-2">New Tenure: {tenure} years</label>
                  <input
                    type="range"
                    min="1"
                    max="7"
                    step="1"
                    value={tenure}
                    onChange={(e) => setTenure(Number(e.target.value))}
                    className="w-full"
                  />
                </div>
              </div>
            </div>
            <div className="flex-1 bg-white p-6 rounded-xl shadow-md text-center">
              <Calculator className="w-12 h-12 text-green-600 mx-auto mb-4" />
              <p className="text-gray-600 mb-2">Estimated Monthly EMI (Refinanced)</p>
              <p className="text-4xl font-bold text-green-700">₹{emi.toLocaleString()}</p>
              <div className="mt-4 text-sm text-gray-500">
                <p>Total Interest Payable: ₹{totalInterest.toLocaleString()}</p>
                <p>Total Payment: ₹{totalPayable.toLocaleString()}</p>
              </div>
              <button className="mt-4 bg-green-600 hover:bg-green-700 text-white font-semibold px-6 py-2 rounded-full transition duration-300">
                Apply Now
              </button>
            </div>
          </div>
        </div>
        <div className="mb-16 text-center animate-fade-up animation-delay-400">
          <p className="text-lg font-semibold text-green-700 bg-green-50 inline-block px-6 py-3 rounded-full shadow-sm">
            Car Refinance Interest Rate - Starting from 8.75%*<br />
            We compare and offer the best rate possible!
          </p>
        </div>
        <div className="mb-16 bg-white rounded-xl shadow-md p-8 animate-fade-up animation-delay-600">
          <h2 className="text-3xl font-bold text-green-700 mb-8 text-center">Eligibility Criteria:</h2>
          <p className="text-gray-600 mb-6 text-center">
            Eligibility for car refinance varies by lender, but common criteria include:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              "Age: 21 – 65 years at loan maturity",
              "Employment: Salaried with minimum 1 year in current job, or self-employed with 2+ years of business continuity",
              "Credit Score: Preferably 700 or above; higher scores improve rate offers",
              "Income: Sufficient to support the new EMI (usually EMI should not exceed 50% of monthly income)",
              "Existing Car Loan: Should have been active for at least 6-12 months with a good repayment track record",
              "Car Age: Typically less than 7-10 years from first registration (depends on lender)",
              "Outstanding Loan Amount: Should be within the lender's refinancing limit (often up to 80-90% of car's current market value)"
            ].map((item, idx) => (
              <div key={idx} className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                <span className="text-gray-700">{item}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="mb-16 animate-fade-up animation-delay-800">
          <h2 className="text-3xl font-bold text-green-700 mb-8 text-center">Car Refinance Options:</h2>
          <div className="space-y-6">
            {[
              {
                icon: Percent,
                title: "Lower Interest Rate Refinance",
                desc: "If interest rates have dropped or your credit score has improved, refinancing can secure a lower rate, reducing your monthly EMI and overall interest cost."
              },
              {
                icon: Clock,
                title: "Tenure Adjustment Refinance",
                desc: "Extend the loan tenure to lower EMIs (if cash flow is tight) or shorten it to pay off the loan faster and save on interest."
              },
              {
                icon: DollarSign,
                title: "Cash‑Out Refinance",
                desc: "If your car's value exceeds the outstanding loan amount, you can refinance for a higher amount and receive the difference in cash for other needs."
              },
              {
                icon: RefreshCw,
                title: "Balance Transfer with Top‑Up",
                desc: "Transfer your existing car loan to a new lender and also get an additional top‑up loan amount based on your repayment capacity and car value."
              },
              {
                icon: Shield,
                title: "Rate Reduction via Balance Transfer",
                desc: "Simply move your outstanding loan to a lender offering a lower rate, keeping the same tenure. Often results in significant interest savings."
              },
              {
                icon: Award,
                title: "Pre‑Approved Refinance",
                desc: "For existing customers of some banks or NBFCs, pre‑approved refinance offers come with minimal documentation and faster processing."
              }
            ].map((type, idx) => (
              <div key={idx} className="flex items-start gap-4 pb-4 border-b border-gray-100 last:border-0">
                <type.icon className="w-8 h-8 text-green-600 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-1">{type.title}</h3>
                  <p className="text-gray-600">{type.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="mb-16 bg-white rounded-xl shadow-md p-8 animate-fade-up animation-delay-1000">
          <h2 className="text-3xl font-bold text-green-700 mb-8 text-center">Documents Required</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              "KYC Documents: PAN Card, Aadhaar Card, Passport/Voter ID (self‑attested)",
              "Proof of Income: Salary slips (last 3 months) and bank statements (last 6 months) for salaried; ITR (last 2 years) and bank statements for self‑employed",
              "Existing Car Loan Details: Loan account statement showing outstanding balance, repayment history, and lender details",
              "Vehicle Documents: Registration Certificate (RC), Insurance policy, and (if available) invoice of the car",
              "No Objection Certificate (NOC): From the existing lender confirming that the vehicle is free from encumbrance after refinance settlement",
              "Employment/Business Proof: Employment ID card or business registration proof"
            ].map((item, idx) => (
              <div key={idx} className="flex items-start gap-3">
                <FileText className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                <span className="text-gray-700">{item}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white rounded-2xl p-8 shadow-lg text-center animate-fade-up animation-delay-1200">
          <h2 className="text-3xl font-bold text-green-700 mb-4">We are Here to Help You</h2>
          <p className="text-gray-600 mb-6">
             CashPro Financial Services makes car refinance simple. We compare offers from multiple lenders, negotiate the best interest rates, and guide you through documentation and transfer processes—all at zero cost. Lower your EMIs, save on interest, and gain peace of mind with our expert assistance.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div>
              <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide">APPLY FOR LOAN</p>
              <p className="text-gray-800 font-medium mt-1">Refinance Your Car Loan?</p>
              <button className="mt-2 bg-green-600 hover:bg-green-700 text-white font-semibold px-6 py-2 rounded-full transition duration-300 shadow">
                Apply Now
              </button>
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide">CALL US AT</p>
              <a href="tel:+918887141052" className="text-lg font-bold text-green-600 hover:underline">
                +91 88871 41052
              </a>
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide">MAIL US AT</p>
              <a href="mailto:info@cashpro.co.in" className="text-lg font-bold text-green-600 hover:underline">
                info@cashpro.co.in
              </a>
            </div>
          </div>
          <Link to="/contact" className="inline-block text-green-600 hover:underline font-medium">
            Contact Us →
          </Link>
        </div>
      </div>
      <style>{`
        @keyframes fade-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-up {
          animation: fade-up 0.6s ease-out forwards;
        }
        .animation-delay-200 {
          animation-delay: 0.2s;
          opacity: 0;
          animation-fill-mode: forwards;
        }
        .animation-delay-400 {
          animation-delay: 0.4s;
          opacity: 0;
          animation-fill-mode: forwards;
        }
        .animation-delay-600 {
          animation-delay: 0.6s;
          opacity: 0;
          animation-fill-mode: forwards;
        }
        .animation-delay-800 {
          animation-delay: 0.8s;
          opacity: 0;
          animation-fill-mode: forwards;
        }
        .animation-delay-1000 {
          animation-delay: 1s;
          opacity: 0;
          animation-fill-mode: forwards;
        }
        .animation-delay-1200 {
          animation-delay: 1.2s;
          opacity: 0;
          animation-fill-mode: forwards;
        }
      `}</style>
    </div>
  );
};

export default CarRefinance;